<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {nextTick, onMounted, onUnmounted, ref, watch} from 'vue'

import {PRECALC_DATA} from '@/components/harmonika'

import {getLayoutAwareKeyLabelSync, isReservedKey} from './keyboardLayout'
import {focusWithoutScroll} from '@/shared/focus';

interface KeyCaptureState {
  rowIdx: number
  btnIdx: number
}

const {
  keyMappings,
  layoutMap,
} = defineProps<{
  keyMappings: string[][]
  layoutMap: Map<string, string>
}>()

const emit = defineEmits<{
  mappingChange: [rowIdx: number, btnIdx: number, keyCode: string]
  resetToDefaults: []
  close: []
}>()

const capturing = ref<KeyCaptureState | null>(null)
const error = ref<string | null>(null)
const captureButton = ref<HTMLButtonElement | null>(null)

watch(capturing, async (nextCapture) => {
  if (!nextCapture) return
  await nextTick()
  focusWithoutScroll(captureButton.value)
})

function startCapture(rowIdx: number, btnIdx: number) {
  capturing.value = {rowIdx, btnIdx}
  error.value = null
}

function cancelCapture() {
  capturing.value = null
  error.value = null
}

function getKeyLabel(rowIdx: number, btnIdx: number) {
  const row = keyMappings[rowIdx]
  const code = row?.[btnIdx] ?? ''
  return code ? getLayoutAwareKeyLabelSync(code, layoutMap) : '-'
}

function handleKeyDown(event: KeyboardEvent) {
  if (!capturing.value) return

  event.preventDefault()
  event.stopPropagation()

  if (event.code === 'Escape') {
    cancelCapture()
    return
  }

  if (isReservedKey(event.code)) {
    error.value = 'Space ist für die Balgsteuerung reserviert'
    return
  }

  emit('mappingChange', capturing.value.rowIdx, capturing.value.btnIdx, event.code)
  cancelCapture()
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
    <section class="relative max-h-[90dvh] w-full max-w-4xl overflow-y-auto rounded-xl border border-stone-600 bg-stone-800 p-6 shadow-2xl">
      <button
        type="button"
        class="absolute top-4 right-4 text-stone-400 transition-colors hover:text-white"
        @click="emit('close')"
      >
        <Icon icon="lucide:x" class="text-2xl"/>
      </button>

      <h2 class="mb-4 flex items-center gap-2 text-2xl font-bold text-amber-500">
        <Icon icon="lucide:keyboard" class="text-2xl"/>
        Tastatur Belegung
      </h2>

      <div class="mb-4 space-y-1 text-sm text-stone-400">
        <p>Klicke auf eine Taste, um sie neu zu belegen. Dann drücke die gewünschte Taste.</p>
        <p class="text-xs text-emerald-500">
          Unterstützt alle Tasten inkl. Strg, Enter, Pfeiltasten und Funktionstasten.
        </p>
      </div>

      <div
        v-if="error"
        class="mb-4 rounded border border-red-500 bg-red-900/50 p-2 text-sm text-red-300"
      >
        {{ error }}
      </div>

      <div
        v-if="capturing"
        class="mb-4 rounded border border-blue-500 bg-blue-900/50 p-3 text-center text-blue-300"
      >
        <p class="mb-2">
          Drücke eine Taste für Reihe {{ capturing.rowIdx + 1 }}, Knopf {{ capturing.btnIdx + 1 }}
        </p>
        <button
          ref="captureButton"
          type="button"
          class="animate-pulse rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-500"
          @click="cancelCapture"
        >
          Warte auf Tastendruck... (Esc zum Abbrechen)
        </button>
      </div>

      <div class="space-y-4">
        <div
          v-for="(row, rowIdx) in PRECALC_DATA"
          :key="row.rowId"
          class="space-y-2"
        >
          <label class="text-xs font-bold text-stone-500 uppercase">
            Reihe {{ rowIdx + 1 }} ({{ row.buttons.length }} Knöpfe)
          </label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="(_, btnIdx) in row.buttons"
              :key="btnIdx"
              type="button"
              class="min-w-[50px] rounded border px-2 py-2 font-mono text-xs transition-all"
              :class="capturing?.rowIdx === rowIdx && capturing?.btnIdx === btnIdx
                ? 'animate-pulse border-blue-400 bg-blue-600 text-white'
                : 'border-stone-600 bg-stone-900 text-stone-300 hover:border-amber-500 hover:bg-stone-700'"
              :title="`Knopf ${btnIdx + 1}`"
              @click="startCapture(rowIdx, btnIdx)"
            >
              <span class="mb-1 block text-[10px] text-stone-500">
                {{ btnIdx + 1 }}
              </span>
              <span class="block font-bold">
                {{ getKeyLabel(rowIdx, btnIdx) }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="mt-6 flex items-center justify-between border-t border-stone-700 pt-4">
        <button
          type="button"
          class="flex items-center gap-2 rounded bg-stone-700 px-4 py-2 text-stone-300 hover:bg-stone-600"
          @click="emit('resetToDefaults')"
        >
          <Icon icon="lucide:rotate-ccw" class="text-base"/>
          Zurücksetzen
        </button>
        <button
          type="button"
          class="rounded bg-emerald-600 px-6 py-2 font-bold text-white hover:bg-emerald-500"
          @click="emit('close')"
        >
          Schließen
        </button>
      </div>
    </section>
  </div>
</template>
